import React from "react";
import { Link } from "react-router-dom";
import { Trash, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { useCart } from "@/lib/cart";
import { formatGHS } from "@/lib/format";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotalGHS } = useCart();

  return (
    <div data-testid="cart-page" className="bg-[#0a0a0a] text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <Link to="/products" data-testid="cart-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-[#FFC107] mb-6">
          <ArrowLeft size={14} weight="bold" /> Continue shopping
        </Link>
        <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tight mb-12">Your Cart</h1>

        {items.length === 0 ? (
          <div className="border border-white/10 p-12 text-center" data-testid="cart-empty">
            <p className="text-zinc-400 mb-6">Your cart is empty. Time to lock something down.</p>
            <Link to="/products" className="inline-flex items-center gap-2 h-12 px-6 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm">
              Browse hardware <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-4">
              {items.map(({ product, quantity }) => (
                <div
                  key={product.id}
                  data-testid={`cart-item-${product.slug}`}
                  className="bg-[#141414] border border-white/10 p-4 flex gap-4 items-center"
                >
                  <img src={product.image_url} alt={product.name} className="w-24 h-24 object-cover" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${product.slug}`} className="font-display font-bold hover:text-[#FFC107]">
                      {product.name}
                    </Link>
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{product.category}</div>
                    <div className="font-mono-accent text-[#FFC107] mt-2">{formatGHS(product.price_ghs)}</div>
                  </div>
                  <div className="flex items-center border border-white/15 h-10">
                    <button data-testid={`cart-dec-${product.slug}`} onClick={() => updateQty(product.id, quantity - 1)} className="w-10 h-10 hover:text-[#FFC107]">−</button>
                    <span className="w-8 text-center font-bold">{quantity}</span>
                    <button data-testid={`cart-inc-${product.slug}`} onClick={() => updateQty(product.id, quantity + 1)} className="w-10 h-10 hover:text-[#FFC107]">+</button>
                  </div>
                  <button data-testid={`cart-remove-${product.slug}`} onClick={() => removeItem(product.id)} className="text-zinc-500 hover:text-[#D32F2F] p-2">
                    <Trash size={20} />
                  </button>
                </div>
              ))}
            </div>

            <div className="lg:col-span-4">
              <div className="bg-[#141414] border border-white/10 p-6 sticky top-24">
                <h3 className="font-display text-xl font-black uppercase mb-6">Order Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-zinc-400">
                    <span>Subtotal</span>
                    <span className="font-mono-accent text-white">{formatGHS(subtotalGHS)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-400">
                    <span>Shipping</span>
                    <span className="font-mono-accent text-white">Calculated at checkout</span>
                  </div>
                </div>
                <div className="border-t border-white/10 my-5" />
                <div className="flex justify-between items-end mb-6">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Total</span>
                  <span data-testid="cart-total" className="font-display text-3xl font-black text-[#FFC107]">{formatGHS(subtotalGHS)}</span>
                </div>
                <Link
                  to="/checkout"
                  data-testid="checkout-btn"
                  className="w-full h-12 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm flex items-center justify-center gap-2"
                >
                  Checkout <ArrowRight size={18} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
